/**
 * Shared clause text formatting utilities for preview components.
 *
 * Provides rendering for clause text extracted from Word templates, including:
 * - [HEADING]...[/HEADING] markers → styled section headings
 * - [GUIDANCE]...[/GUIDANCE] markers → DraftingNote callouts (toggleable)
 * - [TABLE]...[/TABLE] markers → HTML tables with borders
 * - <<placeholder>> segments → amber placeholder badges
 * - Lettered list detection (a), (b), etc. after colon lines
 * - Legacy table detection for data without markers (fallback)
 *
 * Marker parsing is delegated to the shared parser in utils/clauseTextMarkers
 * (a stray/unmatched marker token is removed and its content kept, matching the
 * backend generation stripper; empty guidance is dropped).
 */

import type { ReactNode } from 'react';
import {
  parseClauseSegments,
  type Segment,
  type TableSegment,
} from '../../utils/clauseTextMarkers';
import DraftingNote from './DraftingNote';

/** Renders a <<placeholder>> as a styled amber badge. */
export function renderPlaceholder(text: string, key: number): ReactNode {
  return (
    <span
      key={key}
      className="inline-block bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-caption px-1.5 py-0.5 rounded font-sans"
    >
      {text}
    </span>
  );
}

/** Renders a line, converting <<...>> segments into placeholder badges. */
export function renderLine(line: string, key: number): ReactNode {
  const parts = line.split(/(<<[^>]+>>)/g);
  const rendered = parts.map((part, j) => {
    if (part.startsWith('<<') && part.endsWith('>>')) {
      return renderPlaceholder(part.slice(2, -2).trim(), j);
    }
    return part;
  });
  return <span key={key}>{rendered}</span>;
}

/**
 * Adds lettered markers (a), (b), etc. to consecutive lines ending with ";"
 * that follow a line ending with ":". Mirrors Word auto-numbered lists.
 */
export function addLetterMarkers(lines: string[]): string[] {
  const result: string[] = [];
  let i = 0;
  while (i < lines.length) {
    result.push(lines[i]);
    if (lines[i].trim().endsWith(':')) {
      const listStart = i + 1;
      let listEnd = listStart;
      while (listEnd < lines.length) {
        const trimmed = lines[listEnd].trim();
        if (!trimmed) break;
        const isContinuation =
          trimmed.endsWith(';') ||
          trimmed.endsWith('; and') ||
          trimmed.endsWith('; or') ||
          (listEnd > listStart && (trimmed.endsWith('.') || trimmed.endsWith('.;')));
        if (!isContinuation && listEnd === listStart) break;
        listEnd++;
        if (trimmed.endsWith('.') || trimmed.endsWith('.;')) break;
        if (!isContinuation) break;
      }
      const listLength = listEnd - listStart;
      if (listLength >= 2) {
        for (let j = listStart; j < listEnd; j++) {
          const letter = String.fromCharCode(97 + (j - listStart));
          result.push(`(${letter}) ${lines[j].trim()}`);
        }
        i = listEnd;
        continue;
      }
    }
    i++;
  }
  return result;
}

/** Renders a [HEADING] marker as a styled section heading with clause number. */
function renderHeading(content: string, key: number): ReactNode {
  // Split on tab to separate clause number from title
  const tabIdx = content.indexOf('\t');
  const number = tabIdx >= 0 ? content.slice(0, tabIdx) : null;
  const title = tabIdx >= 0 ? content.slice(tabIdx + 1) : content;

  return (
    <div
      key={`heading-${key}`}
      className="border-b border-slate-300 dark:border-slate-600 pb-2 mb-3"
    >
      <p className="font-bold text-slate-900 dark:text-slate-100">
        {number && (
          <span className="text-slate-500 dark:text-slate-400 mr-2">
            {number}
          </span>
        )}
        {title}
      </p>
    </div>
  );
}

/** Renders a [GUIDANCE] segment as a drafting-note callout (or nothing when hidden). */
function renderGuidance(
  content: string,
  key: string,
  showGuidance: boolean
): ReactNode {
  if (!showGuidance) return null;
  const lines = content.split('\n').filter((line) => line.trim());
  return (
    <DraftingNote key={`guidance-${key}`}>
      {lines.map((line, i) => (
        <p key={i} className={i > 0 ? 'mt-1' : undefined}>
          {renderLine(line, i)}
        </p>
      ))}
    </DraftingNote>
  );
}

/** Renders the parsed segments of a single table cell. */
function renderCellSegments(cell: Segment[], showGuidance: boolean): ReactNode {
  return cell.map((segment, si) => {
    if (segment.kind === 'guidance') {
      return renderGuidance(segment.text, `cell-${si}`, showGuidance);
    }
    // heading/table segments do not occur inside cells; render any text kind inline
    if (segment.kind === 'table') return null;
    return <span key={si}>{renderLine(segment.text, si)}</span>;
  });
}

/** Renders a structured table segment as a styled HTML table. */
function renderTableSegment(
  table: TableSegment,
  key: number,
  showGuidance: boolean
): ReactNode {
  if (table.rows.length === 0) return null;

  // First row is treated as header
  const [headerRow, ...dataRows] = table.rows;

  return (
    <div key={`table-${key}`} className="overflow-x-auto my-2">
      <table className="w-full text-caption border-collapse">
        <thead>
          <tr className="bg-slate-100 dark:bg-slate-700">
            {headerRow.map((cell, ci) => (
              <th
                key={ci}
                className="px-3 py-2 text-left text-caption font-semibold text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700"
              >
                {renderCellSegments(cell, showGuidance)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr
              key={ri}
              className="border-b border-slate-200 dark:border-slate-700"
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="px-3 py-2 border border-slate-200 dark:border-slate-700"
                >
                  {renderCellSegments(cell, showGuidance)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Legacy table detection: detects consecutive header lines ending with ":"
 * followed by placeholder data rows, and renders as an HTML table.
 * Used as fallback for data without [TABLE] markers.
 */
function tryRenderLegacyTable(lines: string[]): ReactNode | null {
  const headers: string[] = [];
  let idx = 0;

  // Collect consecutive header lines (ending with ":")
  while (
    idx < lines.length &&
    lines[idx].trim().endsWith(':') &&
    !lines[idx].includes('<<')
  ) {
    headers.push(lines[idx].trim().replace(/:$/, ''));
    idx++;
  }

  if (headers.length < 2) return null;

  // Collect data rows grouped by header count
  const rows: string[][] = [];
  let currentRow: string[] = [];
  while (idx < lines.length) {
    const line = lines[idx].trim();
    if (line === '(Repeat rows as needed)' || !line) {
      if (currentRow.length > 0) {
        rows.push(currentRow);
        currentRow = [];
      }
      if (line === '(Repeat rows as needed)') {
        idx++;
        break;
      }
      idx++;
      continue;
    }
    currentRow.push(line);
    if (currentRow.length === headers.length) {
      rows.push(currentRow);
      currentRow = [];
    }
    idx++;
  }
  if (currentRow.length > 0) rows.push(currentRow);

  if (rows.length === 0) return null;

  const footer = lines.slice(idx).filter((l) => l.trim());

  return (
    <div>
      <div className="overflow-x-auto mt-1">
        <table className="w-full text-caption border-collapse">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-700">
              {headers.map((h, i) => (
                <th
                  key={i}
                  className="px-3 py-2 text-left text-caption font-semibold text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr
                key={ri}
                className="border-b border-slate-200 dark:border-slate-700"
              >
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className="px-3 py-2 border border-slate-200 dark:border-slate-700"
                  >
                    {renderLine(cell, ci)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <td
                colSpan={headers.length}
                className="px-3 py-1 text-slate-400 italic text-center border border-slate-200 dark:border-slate-700"
              >
                Repeat rows as needed
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {footer.length > 0 && (
        <div className="mt-3">
          {footer.map((line, i) => {
            if (line.trim().endsWith(':') && !line.includes('<<')) {
              return (
                <p
                  key={i}
                  className="font-semibold text-slate-900 dark:text-slate-100 mt-2"
                >
                  {line}
                </p>
              );
            }
            return <p key={i}>{renderLine(line, i)}</p>;
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Formats clause text for display. Parses [HEADING], [GUIDANCE], and [TABLE]
 * markers via the shared segment parser, falls back to legacy table detection
 * inside paragraph segments, and renders line-by-line with placeholder
 * badges, header styling, and lettered list markers.
 *
 * `showGuidance: false` suppresses drafting-note callouts (the Preview tab's
 * "Show drafting notes" toggle) — everything else renders unchanged.
 */
export function formatClauseText(
  text: string,
  options?: { showGuidance?: boolean }
): ReactNode {
  const showGuidance = options?.showGuidance ?? true;
  const elements: ReactNode[] = [];
  const segments = parseClauseSegments(text);

  segments.forEach((segment, partIdx) => {
    if (segment.kind === 'heading') {
      elements.push(renderHeading(segment.text, partIdx));
      return;
    }

    if (segment.kind === 'guidance') {
      const guidance = renderGuidance(segment.text, `${partIdx}`, showGuidance);
      if (guidance) elements.push(guidance);
      return;
    }

    if (segment.kind === 'table') {
      const table = renderTableSegment(segment, partIdx, showGuidance);
      if (table) elements.push(table);
      return;
    }

    // Regular text block — apply letter markers and render line by line
    const rawLines = segment.text.split('\n');
    const lines = addLetterMarkers(rawLines);

    // Try legacy table detection for data without markers
    const firstHeaderIdx = lines.findIndex(
      (l) =>
        l.trim().endsWith(':') &&
        !l.includes('<<') &&
        l.trim().length > 0
    );

    if (firstHeaderIdx >= 0) {
      let headerCount = 0;
      for (let i = firstHeaderIdx; i < lines.length; i++) {
        if (
          lines[i].trim().endsWith(':') &&
          !lines[i].includes('<<') &&
          lines[i].trim().length > 0
        ) {
          headerCount++;
        } else {
          break;
        }
      }

      if (headerCount >= 2) {
        const before = lines
          .slice(0, firstHeaderIdx)
          .filter((l) => l.trim());
        const tableLines = lines.slice(firstHeaderIdx);
        const table = tryRenderLegacyTable(tableLines);

        if (table) {
          before.forEach((line, i) => {
            elements.push(
              <p key={`${partIdx}-pre-${i}`}>
                {renderLine(line, i)}
              </p>
            );
          });
          elements.push(
            <div key={`${partIdx}-table`}>{table}</div>
          );
          return;
        }
      }
    }

    // Fallback: render line by line
    lines.forEach((line, i) => {
      const lineKey = `${partIdx}-${i}`;

      if (!line.trim()) {
        elements.push(<br key={lineKey} />);
        return;
      }

      if (line.trim() === '(Repeat rows as needed)') {
        elements.push(
          <p key={lineKey} className="text-caption text-slate-400 italic my-1">
            {line.trim()}
          </p>
        );
        return;
      }

      if (line.trim().endsWith(':') && !line.includes('<<')) {
        elements.push(
          <p
            key={lineKey}
            className="font-semibold text-slate-900 dark:text-slate-100 mt-2 first:mt-0"
          >
            {line}
          </p>
        );
        return;
      }

      if (/^\([a-z]\)\s/.test(line.trim())) {
        elements.push(
          <p key={lineKey} className="pl-6">
            {renderLine(line, i)}
          </p>
        );
        return;
      }

      elements.push(
        <p key={lineKey} className="my-1">
          {renderLine(line, i)}
        </p>
      );
    });
  });

  return <>{elements}</>;
}
